/**
 * =========================================================================================
 *     DOBLER DATA SOLUTIONS, LLC - PROPRIETARY AND CONFIDENTIAL
 * =========================================================================================
 * The source code contained herein is owned by Dobler Data Solutions, LLC. The source code
 * and all other copyrightable materials related thereto, including without limitation, all
 * text, logos, designs, graphics, other files, and other code, is protected under Title 17
 * of the United States Code. Any use, including any commercial use, distribution,
 * republication, or display of the source code or any other copyrightable materials related
 * thereto is strictly prohibited. Copyright © 2026 Dobler Data Solutions, LLC.
 * ALL RIGHTS RESERVED.
 * =========================================================================================
 *
 *     File Name       :   AuthProvider.tsx
 *     Description     :   MSAL authentication provider — handles redirect callback, populates Zustand shell store, supports DEV_AUTH_BYPASS in local development.
 *     Module          :   005-web-shell
 *     Component Type  :   Provider
 *
 * -----------------------------------------------------------------------------------------
 *     CHANGE HISTORY
 * -----------------------------------------------------------------------------------------
 *     Version     Date            Author              Description
 * -----------------------------------------------------------------------------------------
 *     1.0         2026-06-23      Peter Dobler        Initial creation
 * -----------------------------------------------------------------------------------------
 *
 *     DEPENDENCIES
 * -----------------------------------------------------------------------------------------
 *     External        :   react @azure/msal-browser
 *     Internal        :   msalConfig useShellStore
 *
 *     EXPORTS
 * -----------------------------------------------------------------------------------------
 *     AuthContext AuthProvider
 *
 * =========================================================================================
 */

import { createContext, useEffect, useState, type ReactNode } from "react";
import { msalInstance } from "./msalConfig.js";
import { useShellStore } from "../store/useShellStore.js";

export interface AuthCaller {
  name: string;
  email: string;
  subject: string;
}

export interface AuthContextValue {
  isAuthenticated: boolean;
  /** True while MSAL is still initializing / processing the redirect callback. Hold before deciding auth state. */
  isInitializing: boolean;
  account: AuthCaller | null;
  signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue>({
  isAuthenticated: false,
  isInitializing: true,
  account: null,
  signOut: async () => undefined,
});

// Evaluated at runtime (not at module-scope constant) so vi.stubEnv works in tests
// and Vite's production build still strips the flag when absent from .env.production.
function isDevBypass(): boolean {
  return (
    import.meta.env["VITE_DEV_AUTH_BYPASS"] === "true" &&
    // Gated on DEV (dev server) not the mode NAME, so custom app modes (spec-016 `rp`) keep the
    // same security property: bypass NEVER survives a build.
    (import.meta.env.DEV || import.meta.env.MODE === "test")
  );
}

const DEV_CALLER: AuthCaller = {
  name: "Dev Bypass User",
  email: "dev@localhost",
  subject: "dev-bypass-subject",
};

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): React.JSX.Element {
  const setCaller = useShellStore((s) => s.setCaller);
  const clearCaller = useShellStore((s) => s.clearCaller);

  const [account, setAccount] = useState<AuthCaller | null>(null);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (isDevBypass()) {
      setAccount(DEV_CALLER);
      setCaller(DEV_CALLER);
      setInitialized(true);
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        await msalInstance.initialize();
        const result = await msalInstance.handleRedirectPromise();

        if (cancelled) return;

        if (result?.account) {
          const caller: AuthCaller = {
            name: result.account.name ?? result.account.username,
            email: result.account.username,
            subject: result.account.localAccountId,
          };
          setAccount(caller);
          setCaller(caller);
        } else {
          const accounts = msalInstance.getAllAccounts();
          const active = accounts[0];
          if (active) {
            const caller: AuthCaller = {
              name: active.name ?? active.username,
              email: active.username,
              subject: active.localAccountId,
            };
            setAccount(caller);
            setCaller(caller);
          }
        }
      } finally {
        if (!cancelled) {
          setInitialized(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [setCaller]);

  const signOut = async (): Promise<void> => {
    if (isDevBypass()) {
      setAccount(null);
      clearCaller();
      return;
    }
    clearCaller();
    setAccount(null);
    await msalInstance.logoutRedirect();
  };

  const value: AuthContextValue = {
    isAuthenticated: account !== null,
    isInitializing: !initialized,
    account,
    signOut,
  };

  if (!initialized) {
    // CRITICAL: do NOT render the router (children) here. React Router would immediately navigate
    // from "/" to "/dashboard", stripping the ?code=&state= query params before MSAL reads them.
    // handleRedirectPromise() would then find no auth code → account stays null → /signin loop.
    return (
      <div className="flex items-center justify-center h-screen text-gray-400 text-sm">
        Signing in…
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
